import React, { useEffect, useState } from "react";
import {
  createApprover,
  createCriterion,
  createRisk,
  createTask,
  deleteApprover,
  deleteCriterion,
  deleteRisk,
  deleteTask,
  editGateCriteria,
  editPhaseGoals,
  editPhase,
  getAssignableUsers,
  getChangeLogs,
  getProgram,
  setProgramBackground,
  setProgramLead,
  setProgramStatus,
  signGate,
  toggleTaskDone,
  updateApprover,
  updateCriterion,
  updateRisk,
  updateSprint,
  updateTaskText,
  materialPurchases,
  inventoryConsumption,
  machineHours,
  externalServices,
  setCostSummary,
  listDocuments,
  uploadDocument,
  deleteDocument,
} from "./api";
import { fmtDate, PhaseStrip, StatusFlag } from "./ProgramsTab";

// ── Shared Round-4 components: justification prompt + audit history ──────────

// Collects the required "why" before a restricted change commits.
function JustificationModal({ title, onConfirm, onClose, busy }) {
  const [why, setWhy] = useState("");
  const [error, setError] = useState("");
  const confirm = async () => {
    if (!why.trim()) {
      setError("A justification is required.");
      return;
    }
    try {
      await onConfirm(why.trim());
    } catch (e) {
      setError(e.message || "Save failed.");
    }
  };
  return (
    <div className="pg-modal-back" onClick={onClose}>
      <div className="pg-modal" onClick={(e) => e.stopPropagation()} style={{ width: 460 }}>
        <h3>{title || "Reason for change"}</h3>
        <div className="pg-help" style={{ marginBottom: 8 }}>
          This change is recorded in the program's audit history. A brief reason
          is required.
        </div>
        <div className="pg-field">
          <textarea
            rows={3}
            value={why}
            onChange={(e) => setWhy(e.target.value)}
            placeholder="e.g. Updated after lab review confirmed higher burn-off variability."
            autoFocus
          />
        </div>
        {error && <div className="pg-error">{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="pg-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="pg-btn pg-btn-primary" onClick={confirm} disabled={busy}>
            {busy ? "Saving…" : "Save change"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Read-only ISO audit history for a program.
function ChangeHistoryModal({ programId, onClose }) {
  const [logs, setLogs] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    getChangeLogs(programId)
      .then((data) => setLogs(Array.isArray(data) ? data : data.results || []))
      .catch((e) => setError(e.message));
  }, [programId]);
  const fmt = (ts) => {
    try {
      return new Date(ts).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch (e) {
      return ts;
    }
  };
  return (
    <div className="pg-modal-back" onClick={onClose}>
      <div
        className="pg-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 760, maxWidth: "calc(100vw - 40px)" }}
      >
        <h3>Change history</h3>
        {error && <div className="pg-error">{error}</div>}
        {!logs && !error && <div className="pg-muted pg-small">Loading…</div>}
        {logs && logs.length === 0 && (
          <div className="pg-muted pg-small">
            No recorded changes yet. Edits to criteria, risks, approvers, phase
            goals, and sprints are logged here.
          </div>
        )}
        {logs && logs.length > 0 && (
          <table className="pg-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Item</th>
                <th>Field</th>
                <th>Change</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id}>
                  <td className="pg-small">{fmt(l.created_at)}</td>
                  <td className="pg-small">
                    {l.user ? l.user.full_name || l.user.username : "—"}
                  </td>
                  <td className="pg-small">
                    {l.entity}
                    {l.entity_ref ? ` ${l.entity_ref}` : ""}
                  </td>
                  <td className="pg-small">{l.field}</td>
                  <td className="pg-small">
                    {l.field === "(created)" ? (
                      <span className="pg-badge pg-badge-green">created</span>
                    ) : l.field === "(deleted)" ? (
                      <span className="pg-badge pg-badge-red">deleted</span>
                    ) : (
                      <>
                        <span className="pg-muted">{l.old_value || "—"}</span> →{" "}
                        <span>{l.new_value || "—"}</span>
                      </>
                    )}
                  </td>
                  <td className="pg-small">{l.justification}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <button className="pg-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const CRITERION_BADGE = {
  met: ["pg-badge-green", "Met"],
  defined: ["pg-badge-gray", "Defined"],
  target_undefined: ["pg-badge-red", "Target undefined"],
  verification_undefined: ["pg-badge-red", "Verification undefined"],
};

function Collapsible({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="pg-section">
      <div className="pg-collapse-head" onClick={() => setOpen(!open)}>
        <span className={`chev ${open ? "open" : ""}`}>▸</span>
        <h3 style={{ margin: 0 }}>{title}</h3>
      </div>
      {open && <div style={{ marginTop: 10 }}>{children}</div>}
    </div>
  );
}

// Editable open-text section (technical / market background). Anyone signed in
// may edit; changes are stamped "last edited by X" (lightweight, no
// justification — that's only for restricted fields).
function BackgroundSection({ title, field, program, onSaved }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(program[field] || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const editedBy =
    field === "technical_background"
      ? program.tech_bg_edited_by
      : program.market_bg_edited_by;
  const editedAt =
    field === "technical_background"
      ? program.tech_bg_edited_at
      : program.market_bg_edited_at;

  const startEdit = () => {
    setValue(program[field] || "");
    setError("");
    setEditing(true);
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const updated = await setProgramBackground(program.id, field, value);
      setEditing(false);
      onSaved(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const fmtWhen = (ts) => {
    if (!ts) return "";
    try {
      return new Date(ts).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (e) {
      return "";
    }
  };

  return (
    <div className="pg-section">
      <div className="pg-collapse-head" onClick={() => setOpen(!open)}>
        <span className={`chev ${open ? "open" : ""}`}>▸</span>
        <h3 style={{ margin: 0 }}>{title}</h3>
      </div>
      {open && (
        <div style={{ marginTop: 10 }}>
          {editing ? (
            <>
              <textarea
                className="pg-field"
                style={{ width: "100%", minHeight: 120, boxSizing: "border-box" }}
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
              {error && <div className="pg-error">{error}</div>}
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                <button
                  className="pg-btn pg-btn-primary pg-btn-sm"
                  disabled={busy}
                  onClick={save}
                >
                  {busy ? "Saving…" : "Save"}
                </button>
                <button
                  className="pg-btn pg-btn-sm"
                  disabled={busy}
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
                {program[field] || (
                  <span className="pg-muted pg-small">
                    Nothing here yet — add background to bring others up to speed.
                  </span>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 8,
                }}
              >
                <button className="pg-btn pg-btn-sm" onClick={startEdit}>
                  Edit
                </button>
                {editedBy && (
                  <span className="pg-muted pg-small">
                    Last edited by {editedBy.full_name || editedBy.username}
                    {editedAt ? ` on ${fmtWhen(editedAt)}` : ""}
                  </span>
                )}
              </div>
            </>
          )}
          <DocumentAttachments
            program={program}
            section={field === "technical_background" ? "technical" : "market"}
          />
        </div>
      )}
    </div>
  );
}

function StatusModal({ program, onClose, onSaved }) {
  const [flag, setFlag] = useState(program.status_flag);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const save = async () => {
    try {
      await setProgramStatus(program.id, flag, comment);
      onSaved();
    } catch (e) {
      setError(e.message);
    }
  };
  return (
    <div className="pg-modal-back" onClick={onClose}>
      <div className="pg-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Update program status</h3>
        <div className="pg-field">
          <label>Status</label>
          <select value={flag} onChange={(e) => setFlag(e.target.value)}>
            <option value="green">Green — on track</option>
            <option value="yellow">Yellow — at risk</option>
            <option value="red">Red — blocked</option>
          </select>
        </div>
        <div className="pg-field">
          <label>Why this color? (required)</label>
          <input
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="One line — e.g. reactor assessment slipping one week"
          />
        </div>
        {error && <div className="pg-error">{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="pg-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="pg-btn pg-btn-primary" disabled={!comment.trim()} onClick={save}>
            Save status
          </button>
        </div>
      </div>
    </div>
  );
}

function GateSignModal({ gate, approver, onClose, onSigned }) {
  const [decision, setDecision] = useState("go");
  const [comment, setComment] = useState("");
  const [conditions, setConditions] = useState("");
  const [error, setError] = useState("");
  const sign = async () => {
    try {
      await signGate(gate.id, {
        approver_id: approver.id,
        decision,
        comment,
        conditions:
          decision === "conditional_go"
            ? conditions.split("\n").map((s) => s.trim()).filter(Boolean)
            : [],
      });
      onSigned();
    } catch (e) {
      setError(e.message);
    }
  };
  return (
    <div className="pg-modal-back" onClick={onClose}>
      <div className="pg-modal" onClick={(e) => e.stopPropagation()}>
        <h3>
          {gate.name} — sign as {approver.name}
        </h3>
        <div className="pg-field">
          <label>Decision</label>
          <select value={decision} onChange={(e) => setDecision(e.target.value)}>
            <option value="go">Go</option>
            <option value="conditional_go">Conditional Go</option>
            <option value="no_go">No-Go</option>
          </select>
        </div>
        <div className="pg-field">
          <label>Rationale (required — this becomes the audit trail)</label>
          <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
        {decision === "conditional_go" && (
          <div className="pg-field">
            <label>Conditions — one per line (required, each becomes a tracked item)</label>
            <textarea
              rows={3}
              value={conditions}
              onChange={(e) => setConditions(e.target.value)}
              placeholder={"Quantified uniformity spec written by Sprint 2.2"}
            />
          </div>
        )}
        {error && <div className="pg-error">{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="pg-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="pg-btn pg-btn-primary" disabled={!comment.trim()} onClick={sign}>
            Record decision
          </button>
        </div>
      </div>
    </div>
  );
}

export function SprintBox({ sprint, onChanged, editable = true }) {
  const [open, setOpen] = useState(false);
  const [newTask, setNewTask] = useState("");
  const [objective, setObjective] = useState(null);
  const [editTaskId, setEditTaskId] = useState(null);
  const [editTaskText, setEditTaskText] = useState("");
  const [pending, setPending] = useState(null); // {label, run(why)}
  const [busy, setBusy] = useState(false);

  // Completion toggle: open to anyone, no justification.
  const toggle = async (task) => {
    await toggleTaskDone(task.id, !task.done);
    onChanged();
  };

  // Restricted ops go through the justification modal.
  const stage = (label, run) => setPending({ label, run });
  const runStaged = async (why) => {
    setBusy(true);
    try {
      await pending.run(why);
      setPending(null);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const askAddTask = async () => {
    if (!newTask.trim()) return;
    const text = newTask.trim();
    // Adding a task is free — no justification prompt (only edits/removals are justified).
    setBusy(true);
    try {
      await createTask(sprint.id, text, sprint.tasks.length, "");
      setNewTask("");
      onChanged();
    } finally {
      setBusy(false);
    }
  };
  const askEditTask = () => {
    const id = editTaskId;
    const text = editTaskText.trim();
    stage("Edit task", async (why) => {
      await updateTaskText(id, text, why);
      setEditTaskId(null);
      setEditTaskText("");
    });
  };
  const askDeleteTask = (task) =>
    stage(`Delete task`, async (why) => {
      await deleteTask(task.id, why);
    });
  const askSaveObjective = () => {
    const obj = objective;
    stage("Edit sprint objective", async (why) => {
      await updateSprint(sprint.id, { objective: obj }, why);
      setObjective(null);
    });
  };

  const done = sprint.percent_complete === 100 && sprint.tasks.length > 0;
  return (
    <div className={`pg-sprint-box ${open ? "open" : ""}`} onClick={() => !open && setOpen(true)}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <strong className="pg-small">Sprint {sprint.number}</strong>
        <span className="pg-small pg-muted">
          {fmtDate(sprint.start)}–{fmtDate(sprint.end)}
        </span>
      </div>
      {objective === null ? (
        <div className="pg-small pg-muted" style={{ margin: "3px 0 8px" }}>
          {sprint.objective}
          {open && editable && (
            <button
              className="pg-btn pg-btn-sm"
              style={{ marginLeft: 8 }}
              onClick={(e) => {
                e.stopPropagation();
                setObjective(sprint.objective);
              }}
            >
              Edit
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", gap: 6, margin: "6px 0" }} onClick={(e) => e.stopPropagation()}>
          <input
            style={{ flex: 1, border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "4px 8px", background: "var(--pg-card-bg)", color: "var(--pg-text)" }}
            value={objective}
            onChange={(e) => setObjective(e.target.value)}
          />
          <button className="pg-btn pg-btn-sm pg-btn-primary" onClick={askSaveObjective}>
            Save
          </button>
          <button className="pg-btn pg-btn-sm" onClick={() => setObjective(null)}>
            Cancel
          </button>
        </div>
      )}
      <div className={`pg-progress pg-progress-sm ${done ? "pg-progress-done" : ""}`}>
        <div style={{ width: `${sprint.percent_complete}%` }} />
      </div>
      {open && (
        <div style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
          {sprint.tasks.map((t) =>
            editTaskId === t.id ? (
              <div key={t.id} style={{ display: "flex", gap: 6, margin: "4px 0" }}>
                <input
                  style={{ flex: 1, border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "4px 6px", background: "var(--pg-card-bg)", color: "var(--pg-text)" }}
                  value={editTaskText}
                  onChange={(e) => setEditTaskText(e.target.value)}
                />
                <button className="pg-btn pg-btn-sm pg-btn-primary" onClick={askEditTask}>Save</button>
                <button className="pg-btn pg-btn-sm" onClick={() => setEditTaskId(null)}>Cancel</button>
              </div>
            ) : (
              <div className="pg-task pg-small" key={t.id} style={{ alignItems: "center" }}>
                <input type="checkbox" checked={t.done} onChange={() => toggle(t)} />
                <span className={t.done ? "done" : ""} style={{ flex: 1 }}>{t.text}</span>
                {open && editable && (
                  <>
                    <button className="pg-btn pg-btn-sm" onClick={() => { setEditTaskId(t.id); setEditTaskText(t.text); }}>Edit</button>{" "}
                    <button className="pg-btn pg-btn-sm" onClick={() => askDeleteTask(t)}>Delete</button>
                  </>
                )}
              </div>
            )
          )}
          {sprint.tasks.length === 0 && (
            <div className="pg-small pg-muted" style={{ padding: "4px 0" }}>
              No tasks yet.
            </div>
          )}
          {editable && (
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <input
                style={{ flex: 1, border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "4px 8px", background: "var(--pg-card-bg)", color: "var(--pg-text)" }}
                placeholder="Add a task"
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && askAddTask()}
              />
              <button className="pg-btn pg-btn-sm" onClick={askAddTask}>
                Add
              </button>
            </div>
          )}
          <div style={{ textAlign: "right", marginTop: 6 }}>
            <button className="pg-btn pg-btn-sm" onClick={() => setOpen(false)}>
              Collapse
            </button>
          </div>
        </div>
      )}
      {pending && (
        <JustificationModal
          title={pending.label}
          busy={busy}
          onConfirm={runStaged}
          onClose={() => !busy && setPending(null)}
        />
      )}
    </div>
  );
}


// Per-bullet editor for a gate's go / no-go criteria (restricted + logged).
function GateCriteriaList({ gate, which, items, onChanged }) {
  const [editingIdx, setEditingIdx] = useState(null);
  const [text, setText] = useState("");
  const [adding, setAdding] = useState(false);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const stage = (label, op, payload) =>
    setPending({
      label,
      run: async (why) => {
        setBusy(true);
        try {
          await editGateCriteria(gate.id, which, op, payload, why);
          setPending(null); setEditingIdx(null); setAdding(false); setText("");
          onChanged();
        } finally { setBusy(false); }
      },
    });

  // Adding is free — no justification prompt (only edit/remove are justified).
  const doAdd = async (payload) => {
    setBusy(true);
    try {
      await editGateCriteria(gate.id, which, "add", payload, "");
      setEditingIdx(null); setAdding(false); setText("");
      onChanged();
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="pg-review-col-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>{which === "go" ? "Go if…" : "No-Go if…"}</span>
        <button className="pg-btn pg-btn-sm" onClick={() => { setAdding(true); setText(""); }}>+ Add</button>
      </div>
      {(items || []).map((c, i) =>
        editingIdx === i ? (
          <div key={i} style={{ display: "flex", gap: 4, margin: "3px 0" }}>
            <input value={text} onChange={(e) => setText(e.target.value)}
              style={{ flex: 1, border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "3px 6px", background: "var(--pg-card-bg)", color: "var(--pg-text)", fontSize: "12.5px" }} />
            <button className="pg-btn pg-btn-sm pg-btn-primary" onClick={() => stage("Edit gate criterion", "edit", { index: i, value: text })}>Save</button>
            <button className="pg-btn pg-btn-sm" onClick={() => setEditingIdx(null)}>×</button>
          </div>
        ) : (
          <div key={i} className="pg-small" style={{ display: "flex", alignItems: "flex-start", gap: 6, padding: "2px 0" }}>
            <span style={{ flex: 1 }}>• {c}</span>
            <button className="pg-btn pg-btn-sm" onClick={() => { setEditingIdx(i); setText(c); }}>Edit</button>
            <button className="pg-btn pg-btn-sm" onClick={() => stage("Remove gate criterion", "remove", { index: i })}>×</button>
          </div>
        )
      )}
      {adding && (
        <div style={{ display: "flex", gap: 4, margin: "3px 0" }}>
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="New criterion…" autoFocus
            style={{ flex: 1, border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "3px 6px", background: "var(--pg-card-bg)", color: "var(--pg-text)", fontSize: "12.5px" }} />
          <button className="pg-btn pg-btn-sm pg-btn-primary" onClick={() => doAdd({ value: text })}>Add</button>
          <button className="pg-btn pg-btn-sm" onClick={() => setAdding(false)}>×</button>
        </div>
      )}
      {pending && <JustificationModal title={pending.label} busy={busy} onConfirm={pending.run} onClose={() => !busy && setPending(null)} />}
    </div>
  );
}

// Add a new approver (free-text name + role), restricted + logged.
function ApproverEditor({ gate, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const askAdd = async () => {
    if (!name.trim()) return;
    const n = name.trim(), r = role.trim();
    // Adding is free — no justification prompt (only edit/remove are justified).
    setBusy(true);
    try {
      await createApprover({ gate: gate.id, name: n, role: r }, "");
      setAdding(false); setName(""); setRole("");
      onChanged();
    } finally { setBusy(false); }
  };

  return (
    <>
      {adding ? (
        <div style={{ display: "flex", gap: 4, margin: "4px 0" }}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" autoFocus
            style={{ flex: 1, border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "3px 6px", background: "var(--pg-card-bg)", color: "var(--pg-text)", fontSize: "12.5px" }} />
          <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role"
            style={{ flex: 1, border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "3px 6px", background: "var(--pg-card-bg)", color: "var(--pg-text)", fontSize: "12.5px" }} />
          <button className="pg-btn pg-btn-sm pg-btn-primary" onClick={askAdd}>Add</button>
          <button className="pg-btn pg-btn-sm" onClick={() => setAdding(false)}>×</button>
        </div>
      ) : (
        <button className="pg-btn pg-btn-sm" style={{ marginTop: 4 }} onClick={() => setAdding(true)}>+ Add approver</button>
      )}
      {pending && <JustificationModal title={pending.label} busy={busy} onConfirm={pending.run} onClose={() => !busy && setPending(null)} />}
    </>
  );
}

function GatePanel({ gate, onChanged, canSign = false }) {
  const [signing, setSigning] = useState(null);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!gate) return null;
  const askRemoveApprover = (a) =>
    setPendingRemove({
      label: `Remove approver ${a.name}`,
      run: async (why) => {
        setBusy(true);
        try {
          await deleteApprover(a.id, why);
          setPendingRemove(null);
          onChanged();
        } finally {
          setBusy(false);
        }
      },
    });
  return (
    <div className="pg-card" style={{ background: "var(--pg-card-bg-alt)", marginTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <strong>{gate.name}</strong>
        <span className="pg-small pg-muted">
          Decision {fmtDate(gate.decision_date)}
          {gate.decision_owner ? ` · ${gate.decision_owner}` : ""}
        </span>
      </div>
      {!gate.is_defined && (
        <span className="pg-badge pg-badge-red" style={{ marginTop: 6 }}>
          Gate criteria not yet defined
        </span>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 10 }}>
        <GateCriteriaList gate={gate} which="go" items={gate.go_criteria} onChanged={onChanged} />
        <GateCriteriaList gate={gate} which="no_go" items={gate.no_go_criteria} onChanged={onChanged} />
      </div>
      <div style={{ marginTop: 12, borderTop: "1px solid var(--pg-border)", paddingTop: 10 }}>
        <div className="pg-review-col-label">Approvers</div>
        {gate.approvers.map((a) => (
          <div
            key={a.id}
            className="pg-small"
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0", gap: 8 }}
          >
            <span style={{ flex: 1 }}>
              {a.name}
              {a.role ? ` · ${a.role}` : ""}
            </span>
            {a.decision === "pending" ? (
              gate.closed_at || !canSign ? (
                <span className="pg-badge pg-badge-gray">Pending</span>
              ) : (
                <button className="pg-btn pg-btn-sm" onClick={() => setSigning(a)}>
                  Sign
                </button>
              )
            ) : (
              <span
                className={`pg-badge ${
                  a.decision === "go"
                    ? "pg-badge-green"
                    : a.decision === "no_go"
                    ? "pg-badge-red"
                    : "pg-badge-amber"
                }`}
                title={a.comment}
              >
                {a.decision.replace("_", " ")}
              </span>
            )}
            {!gate.closed_at && a.decision === "pending" && (
              <button className="pg-btn pg-btn-sm" onClick={() => askRemoveApprover(a)}>×</button>
            )}
          </div>
        ))}
        {!gate.closed_at && <ApproverEditor gate={gate} onChanged={onChanged} />}
      </div>
      {gate.outcome && (
        <div className="pg-gate-banner">
          Outcome: <strong>{gate.outcome.replace("_", " ")}</strong> · closed{" "}
          {new Date(gate.closed_at).toLocaleDateString()} · state snapshot saved
        </div>
      )}
      {gate.conditions.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div className="pg-review-col-label">Tracked conditions</div>
          {gate.conditions.map((c) => (
            <div key={c.id} className="pg-small" style={{ padding: "2px 0" }}>
              {c.resolved ? "✓" : "○"} {c.text}
            </div>
          ))}
        </div>
      )}
      {signing && (
        <GateSignModal
          gate={gate}
          approver={signing}
          onClose={() => setSigning(null)}
          onSigned={() => {
            setSigning(null);
            onChanged();
          }}
        />
      )}
      {pendingRemove && (
        <JustificationModal
          title={pendingRemove.label}
          busy={busy}
          onConfirm={pendingRemove.run}
          onClose={() => !busy && setPendingRemove(null)}
        />
      )}
    </div>
  );
}

// Editable risk register (Round 4 restricted surface). Owner/EM only — edits
// go through a justification prompt and are logged. Anyone sees the table and
// the buttons; a non-owner gets a clear "not authorized" on save.
const BLANK_RISK = {
  ref_id: "",
  description: "",
  likelihood: 2,
  impact: 2,
  mitigation: "",
  owner: "TBD",
};

function RiskRegister({ program, onChanged }) {
  const risks = [...program.risks].sort((a, b) => b.score - a.score);
  const [editing, setEditing] = useState(null); // risk id or "new"
  const [draft, setDraft] = useState(BLANK_RISK);
  const [pending, setPending] = useState(null); // {label, run}
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const canEdit = program.can_edit;

  const startEdit = (r) => {
    setDraft({ ...r });
    setEditing(r.id);
  };
  const startNew = () => {
    setDraft({ ...BLANK_RISK });
    setEditing("new");
  };
  const cancel = () => {
    setEditing(null);
    setDraft(BLANK_RISK);
  };

  const askSave = () => {
    const payload = {
      ref_id: draft.ref_id,
      description: draft.description,
      likelihood: Number(draft.likelihood),
      impact: Number(draft.impact),
      mitigation: draft.mitigation,
      owner: draft.owner,
    };
    // Adding a risk is free — no justification prompt. Editing stays justified.
    if (editing === "new") {
      (async () => {
        setBusy(true);
        try {
          await createRisk({ ...payload, program: program.id }, "");
          setEditing(null);
          onChanged();
        } finally { setBusy(false); }
      })();
      return;
    }
    setPending({
      label: "Save risk change",
      run: async (why) => {
        setBusy(true);
        try {
          await updateRisk(editing, payload, why);
          setPending(null);
          setEditing(null);
          onChanged();
        } finally {
          setBusy(false);
        }
      },
    });
  };
  const askDelete = (r) => {
    setPending({
      label: `Delete risk ${r.ref_id || ""}`,
      run: async (why) => {
        setBusy(true);
        try {
          await deleteRisk(r.id, why);
          setPending(null);
          onChanged();
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const cell = (key, type = "text", width) => (
    <input
      type={type}
      value={draft[key]}
      min={type === "number" ? 1 : undefined}
      max={type === "number" ? 3 : undefined}
      onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
      style={{
        width: width || "100%",
        border: "1px solid var(--pg-border-strong)",
        borderRadius: 6,
        padding: "4px 6px",
        background: "var(--pg-card-bg)",
        color: "var(--pg-text)",
      }}
    />
  );

  return (
    <div className="pg-section">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0 }}>Risk register</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="pg-btn pg-btn-sm" onClick={() => setShowHistory(true)}>
            Change history
          </button>
          <button
            className="pg-btn pg-btn-sm pg-btn-primary"
            onClick={startNew}
            title={canEdit ? "" : "Only the program lead or an Engineering Manager can add risks"}
          >
            + Add risk
          </button>
        </div>
      </div>
      <table className="pg-table" style={{ marginTop: 10 }}>
        <thead>
          <tr>
            <th>ID</th>
            <th style={{ width: "34%" }}>Risk</th>
            <th>L×I</th>
            <th>Score</th>
            <th style={{ width: "26%" }}>Mitigation</th>
            <th>Owner</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {editing === "new" && (
            <tr>
              <td>{cell("ref_id", "text", 50)}</td>
              <td>{cell("description")}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                {cell("likelihood", "number", 40)}×{cell("impact", "number", 40)}
              </td>
              <td>—</td>
              <td>{cell("mitigation")}</td>
              <td>{cell("owner", "text", 70)}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                <button className="pg-btn pg-btn-sm pg-btn-primary" onClick={askSave}>Save</button>{" "}
                <button className="pg-btn pg-btn-sm" onClick={cancel}>Cancel</button>
              </td>
            </tr>
          )}
          {risks.map((r) =>
            editing === r.id ? (
              <tr key={r.id}>
                <td>{cell("ref_id", "text", 50)}</td>
                <td>{cell("description")}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {cell("likelihood", "number", 40)}×{cell("impact", "number", 40)}
                </td>
                <td>{Number(draft.likelihood) * Number(draft.impact)}</td>
                <td>{cell("mitigation")}</td>
                <td>{cell("owner", "text", 70)}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="pg-btn pg-btn-sm pg-btn-primary" onClick={askSave}>Save</button>{" "}
                  <button className="pg-btn pg-btn-sm" onClick={cancel}>Cancel</button>
                </td>
              </tr>
            ) : (
              <tr key={r.id}>
                <td>{r.ref_id}</td>
                <td>{r.description}</td>
                <td>{r.likelihood}×{r.impact}</td>
                <td>
                  <span
                    className={`pg-badge ${
                      r.score >= 6 ? "pg-badge-red" : r.score >= 3 ? "pg-badge-amber" : "pg-badge-gray"
                    }`}
                  >
                    {r.score}
                  </span>
                </td>
                <td>{r.mitigation}</td>
                <td>{r.owner}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="pg-btn pg-btn-sm" onClick={() => startEdit(r)}>Edit</button>{" "}
                  <button className="pg-btn pg-btn-sm" onClick={() => askDelete(r)}>Delete</button>
                </td>
              </tr>
            )
          )}
        </tbody>
      </table>

      {pending && (
        <JustificationModal
          title={pending.label}
          busy={busy}
          onConfirm={pending.run}
          onClose={() => !busy && setPending(null)}
        />
      )}
      {showHistory && (
        <ChangeHistoryModal programId={program.id} onClose={() => setShowHistory(false)} />
      )}
    </div>
  );
}


const CRITERION_STATUS_OPTS = [
  ["defined", "Defined"],
  ["target_undefined", "Target undefined"],
  ["verification_undefined", "Verification undefined"],
  ["met", "Met"],
];

function CompletionCriteria({ program, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ metric: "", target: "", verification_method: "", status: "defined" });
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const startEdit = (c) => { setDraft({ metric: c.metric, target: c.target, verification_method: c.verification_method, status: c.status }); setEditing(c.id); };
  const startNew = () => { setDraft({ metric: "", target: "", verification_method: "", status: "defined" }); setEditing("new"); };
  const cancel = () => setEditing(null);

  const askSave = () => {
    // Adding a criterion is free — no justification prompt. Editing stays justified.
    if (editing === "new") {
      (async () => { setBusy(true); try {
        await createCriterion({ ...draft, program: program.id }, "");
        setEditing(null); onChanged();
      } finally { setBusy(false); } })();
      return;
    }
    setPending({
      label: "Save criterion change",
      run: async (why) => { setBusy(true); try {
        await updateCriterion(editing, draft, why);
        setPending(null); setEditing(null); onChanged();
      } finally { setBusy(false); } },
    });
  };
  const askDelete = (c) => setPending({
    label: `Delete criterion "${c.metric}"`,
    run: async (why) => { setBusy(true); try { await deleteCriterion(c.id, why); setPending(null); onChanged(); } finally { setBusy(false); } },
  });

  const inp = (key) => (
    <input value={draft[key]} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
      style={{ width: "100%", border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "4px 6px", background: "var(--pg-card-bg)", color: "var(--pg-text)" }} />
  );
  const sel = () => (
    <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}
      style={{ border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "4px 6px", background: "var(--pg-card-bg)", color: "var(--pg-text)" }}>
      {CRITERION_STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );

  return (
    <div className="pg-section">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0 }}>Completion criteria — development is done when every row is met</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="pg-btn pg-btn-sm" onClick={() => setShowHistory(true)}>Change history</button>
          <button className="pg-btn pg-btn-sm pg-btn-primary" onClick={startNew}>+ Add criterion</button>
        </div>
      </div>
      <table className="pg-table" style={{ marginTop: 10 }}>
        <thead><tr>
          <th style={{ width: "20%" }}>Metric</th><th style={{ width: "30%" }}>Target</th>
          <th style={{ width: "28%" }}>Verification</th><th>Status</th><th></th>
        </tr></thead>
        <tbody>
          {editing === "new" && (
            <tr>
              <td>{inp("metric")}</td><td>{inp("target")}</td><td>{inp("verification_method")}</td><td>{sel()}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                <button className="pg-btn pg-btn-sm pg-btn-primary" onClick={askSave}>Save</button>{" "}
                <button className="pg-btn pg-btn-sm" onClick={cancel}>Cancel</button>
              </td>
            </tr>
          )}
          {program.completion_criteria.map((c) => {
            const [cls, label] = CRITERION_BADGE[c.status] || ["pg-badge-gray", c.status];
            return editing === c.id ? (
              <tr key={c.id}>
                <td>{inp("metric")}</td><td>{inp("target")}</td><td>{inp("verification_method")}</td><td>{sel()}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="pg-btn pg-btn-sm pg-btn-primary" onClick={askSave}>Save</button>{" "}
                  <button className="pg-btn pg-btn-sm" onClick={cancel}>Cancel</button>
                </td>
              </tr>
            ) : (
              <tr key={c.id}>
                <td>{c.metric}</td><td>{c.target}</td><td>{c.verification_method}</td>
                <td><span className={`pg-badge ${cls}`}>{label}</span></td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="pg-btn pg-btn-sm" onClick={() => startEdit(c)}>Edit</button>{" "}
                  <button className="pg-btn pg-btn-sm" onClick={() => askDelete(c)}>Delete</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {pending && <JustificationModal title={pending.label} busy={busy} onConfirm={pending.run} onClose={() => !busy && setPending(null)} />}
      {showHistory && <ChangeHistoryModal programId={program.id} onClose={() => setShowHistory(false)} />}
    </div>
  );
}

function PhaseEditor({ phase, onChanged, onClose }) {
  const [name, setName] = useState(phase.name || "");
  const [start, setStart] = useState(phase.start || "");
  const [end, setEnd] = useState(phase.end || "");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const nameChanged = name.trim() !== (phase.name || "");
  const datesChanged = start !== (phase.start || "") || end !== (phase.end || "");

  const save = async (why) => {
    setBusy(true); setErr("");
    try {
      const data = {};
      if (nameChanged) data.name = name.trim();
      if (start !== (phase.start || "")) data.start = start;
      if (end !== (phase.end || "")) data.end = end;
      if (Object.keys(data).length === 0) { onClose(); return; }
      await editPhase(phase.id, data, why || "");
      setPending(null); onChanged(); onClose();
    } catch (e) {
      setErr(e.message || "Save failed.");
    } finally { setBusy(false); }
  };

  const onSave = () => {
    if (!name.trim()) { setErr("Phase name cannot be empty."); return; }
    if (start && end && end < start) { setErr("End date cannot be before start date."); return; }
    // Changing dates (duration) requires a justification; a pure rename does not.
    if (datesChanged) {
      setPending({ label: "Change phase duration", run: save });
    } else {
      save("");
    }
  };

  return (
    <div className="pg-card" style={{ marginTop: 8, padding: 12, border: "1px solid var(--pg-border-strong)" }}>
      <div className="pg-review-col-label" style={{ marginBottom: 6 }}>Edit phase</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <label className="pg-small pg-muted" style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)}
            style={{ border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "4px 6px", background: "var(--pg-card-bg)", color: "var(--pg-text)" }} />
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          <label className="pg-small pg-muted" style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
            Start
            <input type="date" value={start || ""} onChange={(e) => setStart(e.target.value)}
              style={{ border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "4px 6px", background: "var(--pg-card-bg)", color: "var(--pg-text)" }} />
          </label>
          <label className="pg-small pg-muted" style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
            End
            <input type="date" value={end || ""} onChange={(e) => setEnd(e.target.value)}
              style={{ border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "4px 6px", background: "var(--pg-card-bg)", color: "var(--pg-text)" }} />
          </label>
        </div>
        {datesChanged && <div className="pg-small pg-muted" style={{ fontStyle: "italic" }}>Changing the duration will ask for a reason (logged).</div>}
        {err && <div className="pg-small" style={{ color: "var(--pg-red-text, #DC2626)" }}>{err}</div>}
        <div style={{ display: "flex", gap: 6 }}>
          <button className="pg-btn pg-btn-sm pg-btn-primary" disabled={busy} onClick={onSave}>Save</button>
          <button className="pg-btn pg-btn-sm" disabled={busy} onClick={onClose}>Cancel</button>
        </div>
      </div>
      {pending && <JustificationModal title={pending.label} busy={busy} onConfirm={pending.run} onClose={() => !busy && setPending(null)} />}
    </div>
  );
}


function PhaseGoals({ phase, onChanged }) {
  const [editingIdx, setEditingIdx] = useState(null);
  const [text, setText] = useState("");
  const [adding, setAdding] = useState(false);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const stage = (label, op, payload) => setPending({
    label,
    run: async (why) => { setBusy(true); try {
      await editPhaseGoals(phase.id, op, payload, why);
      setPending(null); setEditingIdx(null); setAdding(false); setText(""); onChanged();
    } finally { setBusy(false); } },
  });

  // Adding is free — no justification prompt (only edit/remove are justified).
  const doAdd = async (payload) => {
    setBusy(true);
    try {
      await editPhaseGoals(phase.id, "add", payload, "");
      setEditingIdx(null); setAdding(false); setText(""); onChanged();
    } finally { setBusy(false); }
  };

  return (
    <div style={{ marginBottom: 6 }}>
      <div className="pg-review-col-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>Phase goals</span>
        <button className="pg-btn pg-btn-sm" onClick={() => { setAdding(true); setText(""); }}>+ Add goal</button>
      </div>
      {(phase.goals || []).map((g, i) =>
        editingIdx === i ? (
          <div key={i} style={{ display: "flex", gap: 6, margin: "4px 0" }}>
            <input value={text} onChange={(e) => setText(e.target.value)}
              style={{ flex: 1, border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "4px 6px", background: "var(--pg-card-bg)", color: "var(--pg-text)" }} />
            <button className="pg-btn pg-btn-sm pg-btn-primary" onClick={() => stage("Edit phase goal", "edit", { index: i, value: text })}>Save</button>
            <button className="pg-btn pg-btn-sm" onClick={() => setEditingIdx(null)}>Cancel</button>
          </div>
        ) : (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, margin: "3px 0" }}>
            <span style={{ flex: 1 }}>• {g}</span>
            <button className="pg-btn pg-btn-sm" onClick={() => { setEditingIdx(i); setText(g); }}>Edit</button>
            <button className="pg-btn pg-btn-sm" onClick={() => stage("Remove phase goal", "remove", { index: i })}>Remove</button>
          </div>
        )
      )}
      {adding && (
        <div style={{ display: "flex", gap: 6, margin: "4px 0" }}>
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="New goal…" autoFocus
            style={{ flex: 1, border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "4px 6px", background: "var(--pg-card-bg)", color: "var(--pg-text)" }} />
          <button className="pg-btn pg-btn-sm pg-btn-primary" onClick={() => doAdd({ value: text })}>Add</button>
          <button className="pg-btn pg-btn-sm" onClick={() => setAdding(false)}>Cancel</button>
        </div>
      )}
      {pending && <JustificationModal title={pending.label} busy={busy} onConfirm={pending.run} onClose={() => !busy && setPending(null)} />}
    </div>
  );
}

// Program owner (lead) display + EM-only reassignment.
function OwnerControl({ program, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState(program.program_lead ? String(program.program_lead.id) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Only an Engineering Manager may reassign the lead. can_set_lead is the
  // manager-only signal from the serializer (can_edit would also be true for a
  // non-EM lead, who cannot reassign — the backend enforces EM-only regardless).
  const canReassign = program.can_set_lead;

  const load = () => {
    getAssignableUsers()
      .then((data) => setUsers(Array.isArray(data) ? data : data.results || []))
      .catch(() => setUsers([]));
  };

  const start = () => {
    load();
    setSel(program.program_lead ? String(program.program_lead.id) : "");
    setError("");
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await setProgramLead(program.id, sel === "" ? null : sel);
      setEditing(false);
      onChanged();
    } catch (e) {
      setError(e.message || "Could not update owner.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pg-small" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
      <span className="pg-muted">Owner:</span>
      {editing ? (
        <>
          <select value={sel} onChange={(e) => setSel(e.target.value)}
            style={{ border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "3px 6px", background: "var(--pg-card-bg)", color: "var(--pg-text)" }}>
            <option value="">— Unassigned —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name || u.username}{u.role ? ` · ${u.role.replace(/_/g, " ")}` : ""}
              </option>
            ))}
          </select>
          <button className="pg-btn pg-btn-sm pg-btn-primary" disabled={busy} onClick={save}>
            {busy ? "Saving…" : "Save"}
          </button>
          <button className="pg-btn pg-btn-sm" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
          {error && <span className="pg-error" style={{ margin: 0 }}>{error}</span>}
        </>
      ) : (
        <>
          <strong>
            {program.program_lead
              ? program.program_lead.full_name || program.program_lead.username
              : "Unassigned"}
          </strong>
          {canReassign && (
            <button className="pg-btn pg-btn-sm" onClick={start}>Change</button>
          )}
        </>
      )}
    </div>
  );
}


// ── Round 5: Costs section (phase-tagged, owner/EM-gated, audited) ───────────
// Four cost types share one section with a phase filter. Each type is a small
// editable table using the same JustificationModal + ChangeHistoryModal pattern.
const COST_TYPES = [
  {
    key: "material", label: "Material purchases", api: materialPurchases,
    cols: [
      ["po_number", "PO #", "text"], ["vendor", "Vendor", "text"],
      ["item", "Item", "text"], ["quantity", "Qty", "number"],
      ["unit", "Unit", "text"], ["unit_cost", "Unit $", "number"],
      ["total_cost", "Total $", "number"],
    ],
    blank: { po_number: "", vendor: "", item: "", quantity: "", unit: "", unit_cost: "", total_cost: "" },
  },
  {
    key: "inventory", label: "Inventory consumption", api: inventoryConsumption,
    cols: [
      ["item", "Item", "text"], ["quantity", "Qty", "number"],
      ["unit", "Unit", "text"], ["cost_basis", "Cost basis $", "number"],
    ],
    blank: { item: "", quantity: "", unit: "", cost_basis: "" },
  },
  {
    key: "machine", label: "Machine hours", api: machineHours,
    cols: [
      ["machine_name", "Machine", "text"], ["hours", "Hours", "number"],
      ["material_generated", "Material generated", "text"],
    ],
    blank: { machine_name: "", hours: "", material_generated: "" },
  },
  {
    key: "external", label: "External services", api: externalServices,
    cols: [
      ["vendor", "Lab / vendor", "text"], ["description", "Description", "text"],
      ["cost", "Cost $", "number"],
    ],
    blank: { vendor: "", description: "", cost: "" },
  },
];

function CostTable({ type, program, phaseFilter, rows, onChanged }) {
  const [editing, setEditing] = useState(null); // id | "new"
  const [draft, setDraft] = useState(type.blank);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const phases = program.phases;
  const phaseNum = (phaseId) => {
    const ph = phases.find((p) => p.id === phaseId);
    return ph ? `P${ph.number}` : "—";
  };
  const visible = rows.filter((r) => phaseFilter === "all" || r.phase === phaseFilter);

  const startNew = () => {
    const defaultPhase = phaseFilter !== "all" ? phaseFilter : (phases[0] && phases[0].id);
    setDraft({ ...type.blank, phase: defaultPhase, date: "" });
    setEditing("new");
  };
  const startEdit = (r) => { setDraft({ ...r }); setEditing(r.id); };
  const cancel = () => setEditing(null);

  const askSave = () =>
    setPending({
      label: editing === "new" ? `Add ${type.label.toLowerCase()}` : "Save cost change",
      run: async (why) => {
        setBusy(true);
        try {
          const payload = { ...draft };
          Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
          if (editing === "new") await type.api.create({ ...payload, phase: draft.phase }, why);
          else await type.api.update(editing, payload, why);
          setPending(null); setEditing(null); onChanged();
        } finally { setBusy(false); }
      },
    });
  const askDelete = (r) =>
    setPending({
      label: "Delete cost entry",
      run: async (why) => {
        setBusy(true);
        try { await type.api.remove(r.id, why); setPending(null); onChanged(); }
        finally { setBusy(false); }
      },
    });

  const inp = (key, kind) => (
    <input type={kind === "number" ? "number" : "text"} value={draft[key] ?? ""}
      onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
      style={{ width: "100%", border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "3px 6px", background: "var(--pg-card-bg)", color: "var(--pg-text)", fontSize: "12.5px" }} />
  );

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <strong className="pg-small">{type.label}</strong>
        <button className="pg-btn pg-btn-sm pg-btn-primary" onClick={startNew}>+ Add</button>
      </div>
      <table className="pg-table" style={{ marginTop: 6 }}>
        <thead>
          <tr>
            <th style={{ width: 44 }}>Phase</th>
            {type.cols.map(([k, label]) => <th key={k}>{label}</th>)}
            <th style={{ width: 90 }}>Date</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {editing === "new" && (
            <tr>
              <td>
                <select value={draft.phase} onChange={(e) => setDraft({ ...draft, phase: Number(e.target.value) })}
                  style={{ border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "3px", background: "var(--pg-card-bg)", color: "var(--pg-text)", fontSize: "12px" }}>
                  {phases.map((p) => <option key={p.id} value={p.id}>P{p.number}</option>)}
                </select>
              </td>
              {type.cols.map(([k, , kind]) => <td key={k}>{inp(k, kind)}</td>)}
              <td>{inp("date", "text")}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                <button className="pg-btn pg-btn-sm pg-btn-primary" onClick={askSave}>Save</button>{" "}
                <button className="pg-btn pg-btn-sm" onClick={cancel}>×</button>
              </td>
            </tr>
          )}
          {visible.map((r) =>
            editing === r.id ? (
              <tr key={r.id}>
                <td>{phaseNum(r.phase)}</td>
                {type.cols.map(([k, , kind]) => <td key={k}>{inp(k, kind)}</td>)}
                <td>{inp("date", "text")}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="pg-btn pg-btn-sm pg-btn-primary" onClick={askSave}>Save</button>{" "}
                  <button className="pg-btn pg-btn-sm" onClick={cancel}>×</button>
                </td>
              </tr>
            ) : (
              <tr key={r.id}>
                <td>{phaseNum(r.phase)}</td>
                {type.cols.map(([k]) => <td key={k}>{r[k]}</td>)}
                <td className="pg-small">{r.date || "—"}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="pg-btn pg-btn-sm" onClick={() => startEdit(r)}>Edit</button>{" "}
                  <button className="pg-btn pg-btn-sm" onClick={() => askDelete(r)}>Del</button>
                </td>
              </tr>
            )
          )}
          {visible.length === 0 && editing !== "new" && (
            <tr><td colSpan={type.cols.length + 3} className="pg-small pg-muted" style={{ padding: "6px 10px" }}>No entries.</td></tr>
          )}
        </tbody>
      </table>
      {pending && <JustificationModal title={pending.label} busy={busy} onConfirm={pending.run} onClose={() => !busy && setPending(null)} />}
    </div>
  );
}

function CostsSection({ program, onChanged }) {
  const [data, setData] = useState({ material: [], inventory: [], machine: [], external: [] });
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [showHistory, setShowHistory] = useState(false);
  const [summaryEdit, setSummaryEdit] = useState(false);
  const [summary, setSummary] = useState(program.cost_summary || "");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadCosts = () => {
    Promise.all([
      materialPurchases.list(program.id),
      inventoryConsumption.list(program.id),
      machineHours.list(program.id),
      externalServices.list(program.id),
    ]).then(([m, i, mh, e]) => {
      const arr = (x) => (Array.isArray(x) ? x : x.results || []);
      setData({ material: arr(m), inventory: arr(i), machine: arr(mh), external: arr(e) });
    }).catch(() => {});
  };
  useEffect(() => { loadCosts(); }, [program.id]);

  const refresh = () => { loadCosts(); onChanged && onChanged(); };

  const askSaveSummary = () =>
    setPending({
      label: "Save cost summary",
      run: async (why) => {
        setBusy(true);
        try { await setCostSummary(program.id, summary, why); setPending(null); setSummaryEdit(false); onChanged(); }
        finally { setBusy(false); }
      },
    });

  return (
    <div className="pg-section">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0 }}>Costs</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className="pg-small pg-muted">Phase:</span>
          <select value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value === "all" ? "all" : Number(e.target.value))}
            style={{ border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "3px 6px", background: "var(--pg-card-bg)", color: "var(--pg-text)", fontSize: "12.5px" }}>
            <option value="all">All phases</option>
            {program.phases.map((p) => <option key={p.id} value={p.id}>P{p.number} · {p.name}</option>)}
          </select>
          <button className="pg-btn pg-btn-sm" onClick={() => setShowHistory(true)}>Change history</button>
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        <div className="pg-review-col-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Program cost summary</span>
          {!summaryEdit && (
            <button className="pg-btn pg-btn-sm" onClick={() => { setSummary(program.cost_summary || ""); setSummaryEdit(true); }}>Edit</button>
          )}
        </div>
        {summaryEdit ? (
          <>
            <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={3}
              style={{ width: "100%", boxSizing: "border-box", border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "6px 8px", background: "var(--pg-card-bg)", color: "var(--pg-text)" }} />
            <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
              <button className="pg-btn pg-btn-sm pg-btn-primary" onClick={askSaveSummary}>Save</button>
              <button className="pg-btn pg-btn-sm" onClick={() => setSummaryEdit(false)}>Cancel</button>
            </div>
          </>
        ) : (
          <div className="pg-small" style={{ whiteSpace: "pre-wrap", color: program.cost_summary ? "var(--pg-text)" : "var(--pg-dim)" }}>
            {program.cost_summary || "No summary yet."}
          </div>
        )}
      </div>

      {COST_TYPES.map((t) => (
        <CostTable key={t.key} type={t} program={program} phaseFilter={phaseFilter} rows={data[t.key]} onChanged={refresh} />
      ))}

      <div className="pg-help" style={{ marginTop: 10 }}>
        Quantities, hours, and purchase prices only — no labor rates or margins.
        Financial roll-up lives in the external management report.
      </div>

      {pending && <JustificationModal title={pending.label} busy={busy} onConfirm={pending.run} onClose={() => !busy && setPending(null)} />}
      {showHistory && <ChangeHistoryModal programId={program.id} onClose={() => setShowHistory(false)} />}
    </div>
  );
}

// ── Round 5: Document attachments (any user; stored in object store) ─────────
function DocumentAttachments({ program, section }) {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [desc, setDesc] = useState("");
  const fileRef = React.useRef(null);

  const load = () =>
    listDocuments(program.id, section)
      .then((d) => setDocs(Array.isArray(d) ? d : d.results || []))
      .catch(() => setDocs([]));
  useEffect(() => { load(); }, [program.id, section]);

  const onPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true); setError("");
    try {
      await uploadDocument(program.id, section, file, desc);
      setDesc("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      setError(err.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };
  const remove = async (d) => {
    try { await deleteDocument(d.id); load(); } catch (e) { setError(e.message); }
  };
  const fmtSize = (n) => (n > 1e6 ? (n / 1e6).toFixed(1) + " MB" : Math.max(1, Math.round(n / 1024)) + " KB");

  return (
    <div style={{ marginTop: 10, borderTop: "1px solid var(--pg-border)", paddingTop: 8 }}>
      <div className="pg-review-col-label">Attachments</div>
      {docs && docs.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          {docs.map((d) => (
            <div key={d.id} className="pg-small" style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0" }}>
              <a href={`/api/development${d.download_url.replace("/api/development", "")}`} style={{ color: "var(--pg-accent)", flex: 1, textDecoration: "none" }}>
                📎 {d.original_name}
              </a>
              {d.description && <span className="pg-muted">{d.description}</span>}
              <span className="pg-muted">{fmtSize(d.size)}</span>
              <span className="pg-muted">{d.uploaded_by ? (d.uploaded_by.full_name || d.uploaded_by.username) : ""}</span>
              <button className="pg-btn pg-btn-sm" onClick={() => remove(d)}>×</button>
            </div>
          ))}
        </div>
      )}
      {docs && docs.length === 0 && <div className="pg-small pg-muted" style={{ marginBottom: 6 }}>No attachments yet.</div>}
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input type="text" placeholder="Optional description" value={desc} onChange={(e) => setDesc(e.target.value)}
          style={{ flex: 1, border: "1px solid var(--pg-border-strong)", borderRadius: 6, padding: "4px 6px", background: "var(--pg-card-bg)", color: "var(--pg-text)", fontSize: "12.5px" }} />
        <input ref={fileRef} type="file" onChange={onPick} disabled={uploading} style={{ fontSize: "12px" }} />
        {uploading && <span className="pg-small pg-muted">Uploading…</span>}
      </div>
      {error && <div className="pg-error">{error}</div>}
    </div>
  );
}

export default function ProgramDetail({ programId, onBack }) {
  const [program, setProgram] = useState(null);
  const [phaseId, setPhaseId] = useState(null);
  const [showStatus, setShowStatus] = useState(false);
  const [editingPhase, setEditingPhase] = useState(false);
  const [error, setError] = useState("");

  const load = () =>
    getProgram(programId)
      .then((p) => {
        setProgram(p);
        if (!phaseId && p.phases.length) {
          const today = new Date().toISOString().slice(0, 10);
          const current =
            p.phases.find((ph) => ph.start <= today && today <= ph.end) || p.phases[0];
          setPhaseId(current.id);
        }
      })
      .catch((e) => setError(e.message));

  useEffect(() => {
    load();
  }, [programId]);

  if (error) return <div className="pg-error">{error}</div>;
  if (!program) return <div className="pg-muted pg-wrap">Loading…</div>;

  const phase = program.phases.find((ph) => ph.id === phaseId) || program.phases[0];

  return (
    <div className="pg-wrap">
      <div className="pg-toolbar">
        <button className="pg-btn" onClick={onBack}>
          ← All programs
        </button>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <StatusFlag flag={program.status_flag} comment={program.status_comment} />
          <button className="pg-btn" onClick={() => setShowStatus(true)}>
            Update status
          </button>
        </div>
      </div>

      <h2 style={{ margin: "0 0 4px", fontSize: 19 }}>
        {program.code} — {program.title}
      </h2>
      {program.application && <div className="pg-small pg-muted">{program.application}</div>}
      <p style={{ margin: "10px 0 6px" }}>{program.goal}</p>
      {program.status_comment && (
        <div className="pg-small pg-muted">Status note: {program.status_comment}</div>
      )}
      <OwnerControl program={program} onChanged={load} />
      <div className="pg-progress" style={{ margin: "12px 0 4px" }}>
        <div style={{ width: `${program.percent_complete}%` }} />
      </div>
      <div className="pg-small pg-muted">{program.percent_complete}% of planned tasks complete</div>

      {program.technical_background !== undefined && (
        <BackgroundSection
          title="Technical background"
          field="technical_background"
          program={program}
          onSaved={setProgram}
        />
      )}
      {program.market_background !== undefined && (
        <BackgroundSection
          title="Market background"
          field="market_background"
          program={program}
          onSaved={setProgram}
        />
      )}

      <CompletionCriteria program={program} onChanged={load} />

      <div className="pg-section">
        <h3>Phases</h3>
        <PhaseStrip phases={program.phases} activeId={phase && phase.id} onSelect={(p) => { setPhaseId(p.id); setEditingPhase(false); }} />
        {phase && (
          <div className="pg-card" style={{ marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>
                Phase {phase.number} — {phase.name}
              </strong>
              <span className="pg-small pg-muted" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {fmtDate(phase.start)} – {fmtDate(phase.end)} · {phase.percent_complete}%
                {program.can_edit && !editingPhase && (
                  <button className="pg-btn pg-btn-sm" onClick={() => setEditingPhase(true)}>Edit</button>
                )}
              </span>
            </div>
            {editingPhase && (
              <PhaseEditor phase={phase} onChanged={load} onClose={() => setEditingPhase(false)} />
            )}
            {phase.objective && <p style={{ margin: "8px 0 4px" }}>{phase.objective}</p>}
            {phase.entry_criteria && (
              <div className="pg-small pg-muted">Entry: {phase.entry_criteria}</div>
            )}
            <div style={{ marginTop: 8 }}>
              <PhaseGoals phase={phase} onChanged={load} />
            </div>
            <div className="pg-grid" style={{ marginTop: 14 }}>
              {phase.sprints.map((s) => (
                <SprintBox key={s.id} sprint={s} onChanged={load} />
              ))}
            </div>
            <GatePanel gate={phase.gate} onChanged={load} canSign={program.can_sign_gates} />
          </div>
        )}
      </div>

      <RiskRegister program={program} onChanged={load} />

      <CostsSection program={program} onChanged={load} />

      {showStatus && (
        <StatusModal
          program={program}
          onClose={() => setShowStatus(false)}
          onSaved={() => {
            setShowStatus(false);
            load();
          }}
        />
      )}
    </div>
  );
}
